import React from "react";
import { SearchBar } from "./components/SearchBar";
import { ModeToggle } from "./components/mode-toggle";
import { Gallery } from "./components/Gallery";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import {
    ArrowLeft,
    Zap,
    Sparkles,
    Search,
    Dog,
    Cat,
    Building2,
    Palette,
} from "lucide-react";
import { Button } from "./components/ui/button";
import Footer from "./components/Footer/Footer";
import { AnimatePresence, motion } from "framer-motion";

function App() {
    const navigate = useNavigate();
    const location = useLocation();
    const isHome = location.pathname === "/";

    const quickSearches = [
        { icon: Dog, label: "Golden Retriever", query: "Golden Retriever" },
        { icon: Cat, label: "Orange Cat", query: "Orange Cat" },
        { icon: Building2, label: "City Life", query: "City Life" },
        {
            icon: Palette,
            label: "Minimalist Architecture",
            query: "Minimalist Architecture",
        },
    ];

    return (
        <div className="min-h-screen flex flex-col text-foreground relative overflow-x-hidden font-sans selection:bg-primary/20">
            {/* Background Gradient Blob */}
            <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none bg-background">
                <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-purple-500/20 blur-[120px] animate-float" />
                <div
                    className="absolute top-[40%] -right-[10%] w-[40%] h-[40%] rounded-full bg-blue-500/20 blur-[100px] animate-float"
                    style={{ animationDelay: "-2s" }}
                />
                <div
                    className="absolute bottom-[10%] left-[20%] w-[30%] h-[30%] rounded-full bg-pink-500/20 blur-[80px] animate-float"
                    style={{ animationDelay: "-4s" }}
                />
                {/* Floating Bubbles */}
                <div
                    className="absolute top-[10%] left-[5%] w-6 h-6 rounded-full bg-foreground/20 animate-bubble-float"
                    style={{ animationDelay: "0s" }}
                />
                <div
                    className="absolute top-[20%] right-[10%] w-4 h-4 rounded-full bg-foreground/20 animate-bubble-float"
                    style={{ animationDelay: "-2s" }}
                />
                <div
                    className="absolute top-[50%] left-[15%] w-8 h-8 rounded-full bg-foreground/20 animate-bubble-float"
                    style={{ animationDelay: "-4s" }}
                />
                <div
                    className="absolute bottom-[20%] right-[5%] w-5 h-5 rounded-full bg-foreground/20 animate-bubble-float"
                    style={{ animationDelay: "-6s" }}
                />
                <div
                    className="absolute bottom-[40%] left-[30%] w-7 h-7 rounded-full bg-foreground/20 animate-bubble-float"
                    style={{ animationDelay: "-8s" }}
                />
                <div
                    className="absolute top-[70%] right-[20%] w-3 h-3 rounded-full bg-foreground/20 animate-bubble-float"
                    style={{ animationDelay: "-1s" }}
                />
                <div
                    className="absolute top-[30%] left-[50%] w-6 h-6 rounded-full bg-foreground/20 animate-bubble-float"
                    style={{ animationDelay: "-3s" }}
                />
                <div
                    className="absolute bottom-[60%] right-[30%] w-4 h-4 rounded-full bg-foreground/20 animate-bubble-float"
                    style={{ animationDelay: "-5s" }}
                />
            </div>

            <header className="w-full flex-none z-50">
                {/* On non-home pages, use a flex row for back, search, and theme toggle */}
                {!isHome ? (
                    <div className="backdrop-blur-lg bg-background/70 border-b border-border/50 fixed top-0 left-0 right-0 z-50">
                        <div className="flex items-center justify-between gap-2 px-4 py-3 max-w-7xl mx-auto w-full">
                            <div className="flex-shrink-0">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => navigate("/")}
                                    aria-label="Back to home"
                                    className="hover:bg-primary/10"
                                >
                                    <ArrowLeft className="w-5 h-5" />
                                </Button>
                            </div>
                            <div className="flex-1 min-w-0 mx-4 max-w-2xl">
                                <SearchBar variant="fixed" />
                            </div>
                            <div className="flex-shrink-0">
                                <ModeToggle />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="absolute top-0 right-0 p-6 z-50">
                        <ModeToggle />
                    </div>
                )}
            </header>

            <main className="flex-1 flex flex-col w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                <AnimatePresence mode="wait">
                    <Routes location={location} key={location.pathname}>
                        <Route
                            path="/"
                            element={
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{
                                        duration: 0.6,
                                        ease: "easeOut",
                                    }}
                                    className="w-full flex flex-col items-center justify-center min-h-[85vh] text-center relative z-10"
                                >
                                    <div className="space-y-6 max-w-4xl mx-auto">
                                        <h1 className="text-6xl sm:text-7xl md:text-8xl font-bold tracking-tight pb-2">
                                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500">
                                                Video Discovery
                                            </span>
                                        </h1>

                                        <p className="text-muted-foreground text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed">
                                            Experience the next generation of
                                            video search. Powered by advanced AI
                                            to understand context, mood, and
                                            composition.
                                        </p>

                                        <div className="flex items-center justify-center gap-4 text-xs sm:text-sm text-muted-foreground/70 font-medium uppercase tracking-wider">
                                            <span className="flex items-center gap-1">
                                                <Zap className="w-3 h-3" />{" "}
                                                Lightning fast
                                            </span>
                                            <span className="w-1 h-1 rounded-full bg-muted-foreground/30" />
                                            <span className="flex items-center gap-1">
                                                <Sparkles className="w-3 h-3" />{" "}
                                                AI-powered
                                            </span>
                                            <span className="w-1 h-1 rounded-full bg-muted-foreground/30" />
                                            <span className="flex items-center gap-1">
                                                <Search className="w-3 h-3" />{" "}
                                                Semantic search
                                            </span>
                                        </div>
                                    </div>

                                    <div className="w-full max-w-2xl mt-12 space-y-8">
                                        <SearchBar variant="inline" />

                                        <div className="flex flex-wrap items-center justify-center gap-3">
                                            {quickSearches.map((item) => (
                                                <button
                                                    key={item.label}
                                                    onClick={() =>
                                                        navigate(
                                                            `/gallery?q=${encodeURIComponent(
                                                                item.query,
                                                            )}`,
                                                        )
                                                    }
                                                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50 hover:bg-muted border border-border/50 transition-all duration-200 text-sm text-muted-foreground hover:text-foreground hover:scale-105"
                                                >
                                                    <item.icon className="w-4 h-4" />
                                                    {item.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </motion.div>
                            }
                        />
                        <Route
                            path="/gallery"
                            element={
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="w-full py-8 pt-24"
                                >
                                    <Gallery />
                                </motion.div>
                            }
                        />
                    </Routes>
                </AnimatePresence>
            </main>

            <Footer />
        </div>
    );
}

export default App;
